import { useMutation, useQuery } from '@apollo/react-hooks';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import ClearIcon from '@material-ui/icons/Clear';
import SubjectIcon from '@material-ui/icons/Subject';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Image, StyleSheet, View } from 'react-native';

import { attachment, close, doneCheck } from '../../../../../assets';
import { Modal } from '../../../../components';
import { fonts } from '../../../../constants/theme';
import {
  CheckPhoneNumberExist,
  CheckPhoneNumberExistVariables,
} from '../../../../generated/CheckPhoneNumberExist';
import {
  Gender,
  IdCardDataInput,
  IdCardType,
  StatusVerify,
} from '../../../../generated/globalTypes';
import {
  UpdateOwner,
  UpdateOwnerVariables,
} from '../../../../generated/UpdateOwner';
import { VerificationRequests_requestVerificationAdvance_row as Data } from '../../../../generated/VerificationRequests';
import validateEmail from '../../../../helpers/validateEmail';
import validateIdentity from '../../../../helpers/validateIdentity';
import { ButtonResetOTP, SegmentedControl } from '../../components';
import { Dropdown, UnverifiedCategories } from '../../components/dropdowns';
import { UnverifiedReasonData } from '../../components/dropdowns/UnverifiedCategories';
import { documentType } from '../../constants/documentType';
import { genderType } from '../../constants/genderType';
import { MIN_KTP_LENGTH } from '../../constants/ktpMinLength';
import { colors, spacing } from '../../constants/theme';
import { useSnackbar } from '../../context';
import {
  AttachmentModal,
  Button,
  DatePicker,
  Infotip,
  Text,
  TextInput,
} from '../../core-ui';
import { UPDATE_OWNER } from '../../graphql/mutations';
import { CHECK_PHONE_NUMBER_EXIST } from '../../graphql/queries';
import { checkPhoneNumberFormat, toPascalCase } from '../../helpers';

import AdditionalDataCollapsible from './AdditionalDataCollapsible';

type Attachment = { name: string; image: string };
type Props = {
  data: Data;
  unclaimLoading?: boolean;
  viewMode?: boolean;
  setFormDirty?: (value: boolean) => void;
  onUnclaim?: () => void;
  onUpdate?: () => void;
  /**this prop is used to check if master admin is opening the owner data form from cell row or master edit button.
   * if from cell row then don't show reset otp button.
   * if from master edit button then show reset otp button.
   */
  isMasterAdminRole: boolean;
  isSuperEdit?: boolean;
  isWip?: boolean;
  onClose?: () => void;
};

const ownerStatusOptions = [StatusVerify.UNVERIFIED, StatusVerify.VERIFIED];
const ownerStatusOptionTexts = ownerStatusOptions.map(toPascalCase);

type UserDataForm = {
  gender: Gender;
  birthDate: Date;
  name: string;
  email?: string | null;
  phone: string;
};

const OwnerDataForm = memo((props: Props) => {
  const {
    data,
    unclaimLoading,
    viewMode = false,
    setFormDirty,
    onUnclaim,
    onUpdate,
    isMasterAdminRole,
    isWip = false,
    isSuperEdit,
  } = props;
  const { openSnackbar } = useSnackbar();

  let [selectedAttachment, setSelectedAttachment] = useState<Attachment>({
    image: '',
    name: '',
  });

  //=============================================
  // states
  const [userFormData, setUserFormData] = useState<UserDataForm>({
    birthDate: data.user?.profile?.birthDate,
    email: data.user?.profile?.email || '',
    gender: data.user?.profile?.gender || ('' as Gender),
    name: data.user?.profile?.name || '',
    phone: data.user?.phoneNumber || '',
  });

  let { birthDate, gender, name, phone, email } = userFormData;

  const [debouncedPhone, setDebouncedPhone] = useState('');

  const { data: checkUserPhone } = useQuery<
    CheckPhoneNumberExist,
    CheckPhoneNumberExistVariables
  >(CHECK_PHONE_NUMBER_EXIST, {
    variables: { phoneNumber: debouncedPhone },
    skip:
      !debouncedPhone ||
      !checkPhoneNumberFormat({ phone: debouncedPhone }).valid ||
      debouncedPhone === data.user?.phoneNumber,
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedPhone(phone), 500);
    return () => clearTimeout(timer);
  }, [phone]);

  const phoneValidation = useMemo(
    () =>
      checkPhoneNumberFormat({
        phone: phone,
        registeredSalesman:
          checkUserPhone?.checkPhoneNumberExist.isRegisteredInSalesmanApp,
        registeredTokoapp: checkUserPhone?.checkPhoneNumberExist.exist,
      }),
    [phone, checkUserPhone],
  );

  const [status, setStatus] = useState(
    Math.max(
      ownerStatusOptions.findIndex((val) => val === data.status),
      0,
    ),
  );
  const [note, setNote] = useState(data.note);
  const [modalOpen, setModalOpen] = useState(false);
  let [attachmentModal, setAttachmentModal] = useState(false);

  const openModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const [
    selectedDocumentType,
    setSelectedDocumentType,
  ] = useState<IdCardType | null>(IdCardType.KTP);

  const [idCardNumber, setIdCardNumber] = useState(
    data.user?.profile?.idCardNumber || '',
  );
  const [idCardData, setIdCardData] = useState<IdCardDataInput>({
    address: data.user?.profile?.idCardData.address || '',
    city: data.user?.profile?.idCardData.city || '',
    district: data.user?.profile?.idCardData.district || '',
    urbanVillage: data.user?.profile?.idCardData.urbanVillage || '',
  });
  const [dataFormId, setDataFormId] = useState('');

  const [unverifiedReasonData, setUnverifiedReasonData] = useState<Array<
    UnverifiedReasonData
  > | null>(data.unverifiedReasonData);

  let emailCheck = email === '' ? true : validateEmail(email || '');
  let identityCheck = validateIdentity(idCardNumber);

  const centangPernyataan = data.user?.agreementStatementRegal
    ? data.user.agreementStatementRegal.agreementStatementApprove || false
    : data.user?.profile?.agreementStatement || false;

  let breakpointDate = new Date('September 20, 2021 00:00:00');
  let userCreatedAt = new Date(data.createdAt);
  let ownerAttachments: Array<Attachment> = [];
  if (data.user?.profile?.avatar) {
    ownerAttachments.push({
      name: t(['Swafoto', 'Selfie']),
      image: data.user.profile.avatar,
    });
  }
  if (data.user?.profile?.idCardPhoto) {
    ownerAttachments.push({
      name: t(['Kartu Identitas', 'ID Card']),
      image: data.user.profile.idCardPhoto,
    });
  }
  if (data.user?.store?.proofOfBusiness) {
    ownerAttachments.push({
      name: t(['Bukti Bisnis', 'Proof of Business']),
      image: data.user?.store?.proofOfBusiness,
    });
  }

  const isDataChanged = useMemo(() => {
    return {
      name: !(name === data.user?.profile?.name),
      email: !(email === data.user?.profile?.email),
      gender: !(gender === data.user?.profile?.gender),
      birthDate: !(birthDate === data.user?.profile?.birthDate),
      phone: !(phone === data.user?.phoneNumber),
      status: !(ownerStatusOptions[status] === data.status),
      note: !(note === data.note),
    };
  }, [
    birthDate,
    data.note,
    data.status,
    data.user,
    email,
    gender,
    name,
    note,
    phone,
    status,
  ]);

  const onUpdateError = useCallback(() => {
    openSnackbar({
      variant: 'error',
      message: t([
        'Gagal memperbarui data. Mohon coba lagi nanti.',
        'Failed to update data. Please try again later.',
      ]),
    });
  }, [openSnackbar]);
  const [updateData, { loading }] = useMutation<
    UpdateOwner,
    UpdateOwnerVariables
  >(UPDATE_OWNER, {
    onCompleted: onUpdate,
    onError: onUpdateError,
  });
  const onUpdateData = useCallback(() => {
    let unverifiedReason = unverifiedReasonData?.map(
      ({ documentProblemType, nextAction, problemReason }) => ({
        nextAction,
        reason: problemReason,
        reasonType: documentProblemType,
      }),
    );

    updateData({
      variables: {
        verificationId: data.id,
        ownerData: {
          name,
          phoneNumber: phone,
          email,
          idCardNumber,
          idCardType: selectedDocumentType,
          idCardData: idCardData,
          birthDate: birthDate,
          gender: gender ? gender : Gender.MALE,
          googleSheetFormNumber: dataFormId,
        },
        status: ownerStatusOptions[status],
        note,
        unverifiedReason,
      },
    });
  }, [
    birthDate,
    data.id,
    dataFormId,
    email,
    gender,
    idCardData,
    idCardNumber,
    name,
    note,
    phone,
    selectedDocumentType,
    status,
    unverifiedReasonData,
    updateData,
  ]);

  const updateUserFormData = <K extends keyof UserDataForm>(
    key: K,
    value: UserDataForm[K],
  ) => {
    setFormDirty?.(true);
    setUserFormData((data) => ({ ...data, [key]: value }));
  };

  let isWAValid = data.user?.whatsAppValidation || false;

  const onOpenAttachment = useCallback((image: string, name: string) => {
    setAttachmentModal(true);
    setSelectedAttachment({ name, image });
  }, []);
  const onCloseAttachment = useCallback(() => setAttachmentModal(false), []);

  let isUnverifiedCategoriesFilled =
    isDataChanged.status && status === 0
      ? unverifiedReasonData &&
        unverifiedReasonData.length > 0 &&
        unverifiedReasonData.every(
          ({ documentProblemType, problemReason }) =>
            documentProblemType && problemReason,
        )
      : true;

  let isDataFormIdFilled = isDataChanged.phone ? Boolean(dataFormId) : true;

  const isButtonDisabled = () => {
    let isMandatoryNotFilled =
      !name || !birthDate || !gender || !idCardNumber || !phone || !note;

    let isWaValidAndVerified =
      ownerStatusOptions[status] === StatusVerify.VERIFIED ? isWAValid : true;

    let isPhoneValid = isDataChanged.phone ? phoneValidation.valid : true;

    return (
      !isWaValidAndVerified ||
      !Object.values(isDataChanged).some(Boolean) ||
      isMandatoryNotFilled ||
      (email && !emailCheck) ||
      !isDataFormIdFilled ||
      !identityCheck ||
      !isUnverifiedCategoriesFilled ||
      !isPhoneValid
    );
  };

  const onChangeIdCardData = useCallback(
    (key: keyof IdCardDataInput, value: string) => {
      setFormDirty?.(true);
      setIdCardData((data) => ({ ...data, [key]: value }));
    },
    [setFormDirty],
  );

  const renderUnverifiedCategories = () => {
    return (
      <UnverifiedCategories
        unverifiedReasonData={
          unverifiedReasonData?.length
            ? unverifiedReasonData
            : [
                {
                  documentProblemType: null,
                  nextAction: '',
                  otherReason: '',
                  phoneCategory: null,
                  problemReason: '',
                  storePhotoCategory: null,
                },
              ]
        }
        setUnverifiedReasonData={(newVal) => {
          setUnverifiedReasonData(newVal);
        }}
      />
    );
  };

  return (
    <>
      <AttachmentModal
        open={attachmentModal}
        title={selectedAttachment.name}
        onClose={onCloseAttachment}
        image={selectedAttachment.image}
      />
      <View style={styles.root}>
        {!isSuperEdit && !viewMode && (
          <>
            <View style={styles.title}>
              <Text bold size="s" color="white">
                {t(['Data Pemilik', 'Owner Data'])}
              </Text>
            </View>
            <View style={[styles.topContainer, styles.border]}>
              <View style={[styles.borderTopZero, styles.lPadding]}>
                <Text style={styles.tokoName}>
                  {t(['Nama Toko', 'Store Name'])}
                </Text>
                <Text bold size="m">
                  {data.user?.store?.storeManagerBulk?.storeName || '-'}
                </Text>
              </View>
              <View style={[styles.borderTopZero, styles.lPadding]}>
                <Text style={styles.tokoName}>
                  {t(['Kode Toko', 'Store Code'])}
                </Text>
                <Text bold size="m">
                  {data.user?.store?.storeManagerBulk?.storeCode || '-'}
                </Text>
              </View>
            </View>
          </>
        )}
        <View
          style={
            viewMode || isSuperEdit
              ? styles.viewMode
              : [styles.borderTopZero, styles.border, styles.lPadding]
          }
        >
          {!isSuperEdit && (
            <Text bold size="m" style={styles.lBottomSpacing}>
              {t(['Informasi Pemilik', 'Owner Information'])}
            </Text>
          )}

          <TextInput
            data-testid="owner-verification-name"
            containerStyle={styles.lBottomSpacing}
            value={name}
            onChangeText={(newText) => updateUserFormData('name', newText)}
            label={t(['Nama', 'Name'])}
            required
            disabled={viewMode}
          />
          <TextInput
            data-testid="owner-verification-phone"
            value={phone}
            onChangeText={(newText) => updateUserFormData('phone', newText)}
            label={t(['Nomor HP', 'Mobile'])}
            disabled={!isSuperEdit || viewMode}
            required
            helperText={phoneValidation.reason}
            error={!phoneValidation.valid}
          />
          {!isSuperEdit &&
            (isWAValid ? (
              <View style={[styles.xxsTopSpacing, styles.WAContainer]}>
                <Text bold={true} style={styles.limeGreen}>
                  Nomor WhatsApp Valid
                </Text>
                <CheckBoxIcon htmlColor={'#21F034'} />
              </View>
            ) : (
              <View style={[styles.xxsTopSpacing, styles.WAContainer]}>
                <Text bold={true} style={styles.bloodRed}>
                  Nomor WhatsApp Belum Valid
                </Text>
                <ClearIcon htmlColor={'#EB0001'} />
              </View>
            ))}
          {/** if modal is open from cell click, then don't show the reset otp button.
           * only show reset otp button when opening from "master edit" button for master admin
           */}
          {(!isMasterAdminRole || isSuperEdit) && (
            <ButtonResetOTP
              containerStyle={styles.xxsTopSpacing}
              userId={data.userId || ''}
            />
          )}

          <TextInput
            data-testid="owner-verification-email"
            containerStyle={
              emailCheck ? styles.lBottomSpacing : styles.sBottomSpacing
            }
            value={email}
            onChangeText={(newText) => updateUserFormData('email', newText)}
            label={t(['Email (opsional)', 'Email (optional)'])}
            disabled={viewMode}
          />
          {!emailCheck && (
            <Text bold style={styles.formatEmailSalah}>
              Format Email Salah!
            </Text>
          )}
          <Dropdown
            onChange={(value: IdCardType) => {
              setSelectedDocumentType(value);
            }}
            label={t(['Tipe Dokumen', 'Document Type'])}
            value={selectedDocumentType}
            options={documentType}
            containerStyle={styles.cardType}
            disabled={true}
          />
          <TextInput
            data-testid="owner-verification-id"
            containerStyle={styles.lBottomSpacing}
            value={idCardNumber}
            onChangeText={setIdCardNumber}
            label={t(['Nomor KTP', 'ID Card Number'])}
            error={idCardNumber.length < MIN_KTP_LENGTH || !identityCheck}
            placeholder="16 Digit Nomor Induk Kependudukan/No KTP"
            helperText={
              idCardNumber.length < MIN_KTP_LENGTH
                ? t([
                    'Nomor Kartu Identitas minimal 8 karakter.',
                    'ID number must be minimum of 8 characters.',
                  ])
                : !identityCheck
                ? t(['Format Identitas Salah. ', 'Wrong Identity Format.'])
                : ''
            }
            disabled={viewMode || isSuperEdit}
            onInput={(e) => {
              (e.target as HTMLTextAreaElement).value = (e.target as HTMLTextAreaElement).value
                .toString()
                .slice(0, 19);
            }}
            required
          />
          <View>
            <Dropdown
              onChange={(newText: Gender) =>
                updateUserFormData('gender', newText)
              }
              label={t(['Jenis Kelamin', 'Gender'])}
              value={gender}
              options={genderType}
              containerStyle={styles.cardType}
              disabled={viewMode}
              required
              error={status !== 0 ? gender == null : false}
              helperText={
                status !== 0
                  ? gender == null
                    ? t(['Kolom harus diisi', 'This field must be filled'])
                    : ''
                  : ''
              }
            />
          </View>
          <View>
            <DatePicker
              data-testid="owner-verification-birthdate"
              selectedDate={birthDate}
              onChange={(newDate) => updateUserFormData('birthDate', newDate)}
              label={t(['Tanggal Lahir', 'Birth Date'])}
              style={styles.birthDate}
              disabled={viewMode}
              required={true}
              error={status !== 0 ? birthDate == null : false}
              helperText={
                status !== 0
                  ? birthDate == null
                    ? t(['Kolom harus diisi', 'This field must be filled'])
                    : ''
                  : ''
              }
            />
          </View>

          <View style={styles.lBottomSpacing}>
            <AdditionalDataCollapsible
              data={idCardData}
              onChangeData={onChangeIdCardData}
              disabled={(!isWip && !isMasterAdminRole) || viewMode}
            />
          </View>

          <TextInput
            data-testid="owner-verification-address"
            value={data.user?.store?.storeManagerBulk?.address || ''}
            onChangeText={() => {}}
            label={'Address'}
            disabled={true}
          />
          <View style={styles.provinceAndCityContainer}>
            <TextInput
              data-testid="owner-verification-province"
              value={data.user?.store?.storeManagerBulk?.province || ''}
              onChangeText={() => {}}
              label={'Provinsi'}
              disabled={true}
              containerStyle={styles.provinceAndCityTextBox}
            />
            <TextInput
              data-testid="owner-verification-city"
              value={data.user?.store?.storeManagerBulk?.city || ''}
              onChangeText={() => {}}
              label={'Kabupaten/Kota'}
              disabled={true}
              containerStyle={styles.provinceAndCityTextBox}
            />
          </View>
          {userCreatedAt.getTime() > breakpointDate.getTime() ? (
            <View>
              {centangPernyataan ? (
                <View style={styles.attachmentRow}>
                  <View style={{ paddingBottom: 8 }}>
                    <Image source={doneCheck} style={styles.doneIcon} />
                  </View>
                  <Text bold style={styles.sudahCentangPernyataan}>
                    Sudah Centang Kalimat Pernyataan
                  </Text>
                </View>
              ) : (
                <View style={styles.attachmentRow}>
                  <View style={{ paddingBottom: 8 }}>
                    <Image source={close} style={styles.doneIcon} />
                  </View>
                  <Text bold style={styles.belumCentangPernyataan}>
                    Belum Centang Kalimat Pernyataan
                  </Text>
                </View>
              )}
            </View>
          ) : (
            <View>
              {centangPernyataan ? (
                <View style={styles.attachmentRow}>
                  <View style={{ paddingBottom: 8 }}>
                    <Image source={doneCheck} style={styles.doneIcon} />
                  </View>
                  <Text bold style={styles.sudahCentangPernyataan}>
                    Sudah Centang Kalimat Pernyataan (User Menggunakan Dokumen
                    Bisnis)
                  </Text>
                </View>
              ) : (
                <View style={styles.attachmentRow}>
                  <View style={{ paddingBottom: 8 }}>
                    <Image source={close} style={styles.doneIcon} />
                  </View>
                  <Text bold style={styles.belumCentangPernyataan}>
                    Belum Centang Kalimat Pernyataan (User Menggunakan Dokumen
                    Bisnis)
                  </Text>
                </View>
              )}
            </View>
          )}
          <View style={styles.viewPernyataan}>
            <Text bold style={styles.pernyataanHeader}>
              Kalimat Pernyataan
            </Text>
            <Text style={styles.pernyataan}>
              {centangPernyataan
                ? data.user?.agreementStatementRegal?.agreementStatement || ''
                : ''}
            </Text>
          </View>
        </View>
        {!isSuperEdit && (
          <View
            style={
              viewMode || isSuperEdit
                ? styles.viewMode
                : [styles.borderTopZero, styles.border, styles.lPadding]
            }
          >
            <Text bold size="m" style={styles.lBottomSpacing}>
              {`Attachments (${ownerAttachments.length})`}
            </Text>
            <View style={[styles.row, styles.attachments]}>
              {ownerAttachments.map((item, index) => (
                <Button
                  key={index}
                  titleTextProps={{ color: 'secondaryBlack' }}
                  style={[
                    styles.button,
                    index > 0 ? styles.butonSpacing : undefined,
                  ]}
                  iconAndText
                  icon={
                    <Image source={attachment} style={styles.attachmentIcon} />
                  }
                  preset="transparent"
                  title={item.name}
                  onPress={() => onOpenAttachment(item.image, item.name)}
                />
              ))}
            </View>
          </View>
        )}
        <View
          style={
            viewMode || isSuperEdit
              ? styles.viewMode
              : [styles.borderTopZero, styles.border, styles.lPadding]
          }
        >
          <View
            style={[styles.row, styles.lBottomSpacing, styles.centerAligned]}
          >
            <Text bold size="m" style={{ marginRight: spacing.xxs }}>
              {t(['Status Pemilik', 'OWNER Status'])}
            </Text>
            <Infotip />
          </View>
          <SegmentedControl
            data-testid="owner-verification-status"
            containerStyle={styles.statusContainer}
            selected={status}
            setSelected={setStatus}
            options={ownerStatusOptionTexts}
            disabled={viewMode}
          />
        </View>
        {status === 0 && (
          <View
            style={
              viewMode || isSuperEdit
                ? styles.viewMode
                : [styles.borderTopZero, styles.border, styles.lPadding]
            }
          >
            <Text bold size="m" style={styles.categoryUnverifiedText}>
              Kategori Unverified
            </Text>
            {renderUnverifiedCategories()}
          </View>
        )}
      </View>
      <View
        style={
          isSuperEdit
            ? styles.viewMode
            : [styles.border, styles.bottomContainer]
        }
      >
        <TextInput
          data-testid="owner-verification-note"
          containerStyle={!viewMode && styles.lBottomSpacing}
          value={note}
          onChangeText={setNote}
          leftNode={<SubjectIcon htmlColor={colors.border.primary} />}
          placeholder={t(['Catatan (Wajib)', 'Add Notes (Required)'])}
          multiline
          disabled={viewMode}
          rows={3}
        />
        {isSuperEdit && (
          <>
            <TextInput
              value={dataFormId}
              containerStyle={styles.lBottomSpacing}
              onChangeText={setDataFormId}
              disabled={loading}
              required={isDataChanged.phone}
              label={t([
                'Nomor Data Form di Google Sheet',
                'Form Data Number in Google Sheet',
              ])}
            />
          </>
        )}
        {isSuperEdit ? (
          <View
            style={[
              styles.row,
              styles.lBottomSpacing,
              { justifyContent: 'flex-end' },
            ]}
          >
            <Button
              style={styles.buttonWidth}
              preset="primary"
              title={t(['Simpan', 'Save'])}
              onPress={openModal}
              disabled={isButtonDisabled()}
              isLoading={loading}
            />
          </View>
        ) : (
          !viewMode && (
            <View style={[styles.row, { justifyContent: 'space-between' }]}>
              {onUnclaim && (
                <Button
                  data-testid="owner-verification-unclaim"
                  style={styles.buttonWidth}
                  isLoading={unclaimLoading}
                  preset="secondary"
                  title={t(['Batal Klaim', 'Unclaim'])}
                  onPress={onUnclaim}
                />
              )}
              <Button
                data-testid="owner-verification-submit"
                disabled={isButtonDisabled()}
                isLoading={loading}
                style={styles.buttonWidth}
                preset="primary"
                title={t(['Perbarui Data', 'Update Data'])}
                onPress={onUpdateData}
              />
            </View>
          )
        )}

        <Modal
          open={modalOpen}
          onClose={closeModal}
          title={t(['Simpan Perubahan', 'Save Changes'])}
          titleSize="xl"
          TextComponent={Text}
        >
          <Text style={styles.bottomContainer}>
            {t([
              'Apakah Anda sudah yakin dengan perubahan ini?',
              'Are you sure with this change?',
            ])}
          </Text>
          <View style={styles.confirmationModalButton}>
            <Button
              title={t(['Tidak', 'No'])}
              preset="secondary"
              onPress={closeModal}
            />
            <Button
              title={t(['Ya', 'Yes'])}
              onPress={onUpdateData}
              isLoading={loading}
            />
          </View>
        </Modal>
      </View>
    </>
  );
});

const styles = StyleSheet.create({
  root: { width: 529, backgroundColor: colors.background.white },
  row: { flexDirection: 'row' },
  buttonWidth: {
    width: 230,
  },
  title: {
    height: 42,
    justifyContent: 'center',
    backgroundColor: colors.background.orange,
    alignItems: 'center',
  },
  borderTopZero: {
    borderTopWidth: 0,
  },
  border: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.button.border.primary,
  },
  birthDate: {
    fontSize: fonts.sizes.small,
    paddingBottom: 24,
  },
  lBottomSpacing: {
    marginBottom: spacing.l,
  },
  lPadding: {
    padding: spacing.l,
  },
  sBottomSpacing: {
    marginBottom: spacing.s,
  },
  xxsTopSpacing: {
    marginTop: spacing.xxs,
  },
  button: {
    marginBottom: spacing.s,
    minWidth: 149,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.button.border.primary,
  },
  butonSpacing: { marginLeft: spacing.m },
  bottomContainer: {
    // marginTop: 24,
    padding: 24,
    width: 529,
    backgroundColor: colors.background.white,
  },
  attachmentIcon: { height: 24, width: 24, marginRight: 8 },
  doneIcon: {
    height: 20,
    width: 20,
    marginRight: 2,
  },
  confirmationModalButton: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    paddingTop: spacing.m,
    paddingRight: spacing.l,
    paddingBottom: spacing.m,
  },
  attachments: { flexWrap: 'wrap' },
  tokoName: { paddingBottom: spacing.xxs },
  centerAligned: { alignItems: 'center' },
  statusContainer: { width: '100%' },
  viewMode: {
    borderTopColor: colors.button.border.primary,
    borderTopWidth: 1,
    paddingTop: spacing.l,
    paddingBottom: spacing.m,
  },
  cardType: { paddingBottom: 12 },
  pernyataanHeader: {
    textAlign: 'left',
    width: '80%',
    fontWeight: 'bold',
    fontSize: 16,
    lineHeight: 20,
    paddingBottom: 11,
  },
  pernyataan: {
    textAlign: 'left',
    fontSize: 14,
    lineHeight: 20,
  },
  viewPernyataan: {
    borderTopColor: colors.button.border.primary,
    borderTopWidth: 1,
    paddingTop: 20,
    paddingBottom: 8,
  },
  sudahCentangPernyataan: {
    fontSize: 14,
    color: colors.icon.acceptGreen,
    lineHeight: 17,
    paddingBottom: 26,
  },
  attachmentRow: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    paddingTop: 17,
  },
  belumCentangPernyataan: {
    fontSize: 14,
    color: colors.icon.declineRed,
    lineHeight: 17,
    paddingBottom: 26,
  },
  formatEmailSalah: {
    fontSize: 12,
    color: colors.icon.declineRed,
    lineHeight: 17,
    paddingBottom: 26,
  },
  categoryUnverifiedText: { paddingBottom: 24 },
  topContainer: { flexDirection: 'row', justifyContent: 'space-between' },
  provinceAndCityTextBox: { width: 230 },
  provinceAndCityContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 24,
  },
  WAContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  limeGreen: { color: '#21F034' },
  bloodRed: { color: '#EB0001' },
});

export default OwnerDataForm;
